import React, { useState, useEffect, useCallback } from 'react';
import { useProfile } from '../contexts/ProfileContext';
import { isUsernameAvailable } from '../lib/supabase';
import AvatarUpload from './AvatarUpload';
import { FiSave, FiUser, FiEye, FiEyeOff, FiImage, FiStar, FiSearch } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const ProfileEditor = () => {
  const { profile, saveProfile, loading } = useProfile();
  const [formData, setFormData] = useState({
    username: '',
    display_name: '',
    bio: '',
    avatar: '',
    theme: 'dark-pro',
    is_public: true,
    seo_title: '',
    seo_description: '',
    featured_title: '',
    featured_type: 'media',
  });
  const [saving, setSaving] = useState(false);
  const [usernameChecking, setUsernameChecking] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [usernameError, setUsernameError] = useState('');
  const [saveMessage, setSaveMessage] = useState({ type: '', text: '' });

  const themes = [
    { id: 'minimal-light', name: 'Minimal Light', description: 'Clean and simple' },
    { id: 'dark-pro', name: 'Dark Pro', description: 'Professional dark theme' },
    { id: 'cyber-neon', name: 'Cyber Neon', description: 'Futuristic neon style' },
  ];

  useEffect(() => {
    if (profile) {
      setFormData({
        username: profile.username || '',
        display_name: profile.display_name || '',
        bio: profile.bio || '',
        avatar: profile.avatar || '',
        theme: profile.theme || 'dark-pro',
        is_public: profile.is_public ?? true,
        seo_title: profile.seo_title || '',
        seo_description: profile.seo_description || '',
        featured_title: profile.featured_title || '',
        featured_type: profile.featured_type || 'media',
      });
    }
  }, [profile]);

  // Username validation
  const validateUsername = (username) => {
    if (!username) {
      setUsernameError('');
      setUsernameAvailable(null);
      return false;
    }

    // Format validation: alphanumeric, 3-20 chars
    const usernameRegex = /^[a-zA-Z0-9_-]{3,20}$/;
    if (!usernameRegex.test(username)) {
      setUsernameError('Username must be 3-20 characters, alphanumeric with - or _');
      setUsernameAvailable(false);
      return false;
    }

    setUsernameError('');
    return true;
  };

  // Check username availability
  const checkUsername = useCallback(async (username) => {
    if (!username || !validateUsername(username)) {
      return;
    }

    // Don't check if it's the current username
    if (profile && profile.username === username) {
      setUsernameAvailable(true);
      return;
    }

    setUsernameChecking(true);
    try {
      const { data, error } = await isUsernameAvailable(username);
      if (error) {
        setUsernameAvailable(false);
        setUsernameError('Error checking username availability');
      } else {
        setUsernameAvailable(data.available);
        if (!data.available) {
          setUsernameError('Username is already taken');
        }
      }
    } catch (error) {
      setUsernameAvailable(false);
      setUsernameError('Error checking username');
    } finally {
      setUsernameChecking(false);
    }
  }, [profile]);

  // Debounced username check
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.username) {
        checkUsername(formData.username);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [formData.username, checkUsername]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    // Clear save message on change
    if (saveMessage.text) {
      setSaveMessage({ type: '', text: '' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate username
    if (!validateUsername(formData.username)) {
      setSaveMessage({ type: 'error', text: 'Please fix username errors' });
      return;
    }

    if (usernameAvailable === false) {
      setSaveMessage({ type: 'error', text: 'Username is not available' });
      return;
    }

    setSaving(true);
    setSaveMessage({ type: '', text: '' });

    try {
      const result = await saveProfile(formData);
      if (result.success) {
        setSaveMessage({ type: 'success', text: 'Profile saved successfully!' });
        // Clear message after 3 seconds
        setTimeout(() => setSaveMessage({ type: '', text: '' }), 3000);
      } else {
        setSaveMessage({ type: 'error', text: result.error || 'Error saving profile' });
      }
    } catch (error) {
      setSaveMessage({ type: 'error', text: error.message || 'An error occurred' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Save Message */}
      {saveMessage.text && (
        <div className={`glass p-4 rounded-lg border ${
          saveMessage.type === 'success' 
            ? 'border-lime-500/50 bg-lime-500/10' 
            : 'border-red-500/50 bg-red-500/10'
        }`}>
          <p className={`text-sm font-medium ${
            saveMessage.type === 'success' ? 'text-lime-400' : 'text-red-400'
          }`}>
            {saveMessage.text}
          </p>
        </div>
      )}

      {/* Basic Information */}
      <div className="glass-dark p-6 rounded-xl border border-dark-700">
        <h3 className="text-lg font-semibold text-lime-bright mb-6 flex items-center">
          <SafeIcon icon={FiUser} className="mr-2" />
          Basic Information
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Username */}
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-dark-200 mb-2">
              Username *
            </label>
            <div className="relative">
              <SafeIcon icon={FiUser} className="absolute left-3 top-3 text-dark-400" />
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                required
                className={`pl-10 w-full px-3 py-2 glass rounded-lg text-white placeholder-dark-500 focus:ring-2 focus:ring-lime-500 focus:border-lime-500 border ${
                  usernameError || usernameAvailable === false
                    ? 'border-red-500/50'
                    : usernameAvailable === true
                    ? 'border-lime-500/50'
                    : 'border-dark-700'
                }`}
                placeholder="your-username"
              />
              {usernameChecking && (
                <div className="absolute right-3 top-3">
                  <div className="w-4 h-4 border-2 border-dark-500 border-t-lime-500 rounded-full animate-spin" />
                </div>
              )}
            </div>
            {usernameError && (
              <p className="text-xs text-red-400 mt-1">{usernameError}</p>
            )}
            {usernameAvailable === true && !usernameError && (
              <p className="text-xs text-lime-400 mt-1">✓ Username available</p>
            )}
            <p className="text-xs text-dark-400 mt-1">
              Your unique HubID username (hubid.app/u/{formData.username || 'username'})
            </p>
          </div>

          {/* Display Name */}
          <div>
            <label htmlFor="display_name" className="block text-sm font-medium text-dark-200 mb-2">
              Display Name
            </label>
            <input
              type="text"
              id="display_name"
              name="display_name"
              value={formData.display_name}
              onChange={handleChange}
              className="w-full px-3 py-2 glass rounded-lg text-white placeholder-dark-500 focus:ring-2 focus:ring-lime-500 focus:border-lime-500 border border-dark-700"
              placeholder="Your Name"
            />
          </div>
        </div>

        {/* Bio */}
        <div className="mt-6">
          <label htmlFor="bio" className="block text-sm font-medium text-dark-200 mb-2">
            Bio
          </label>
          <textarea
            id="bio"
            name="bio"
            value={formData.bio}
            onChange={handleChange}
            rows={4}
            maxLength={500}
            className="w-full px-3 py-2 glass rounded-lg text-white placeholder-dark-500 focus:ring-2 focus:ring-lime-500 focus:border-lime-500 border border-dark-700 resize-none"
            placeholder="Tell the world about yourself..."
          />
          <p className="text-xs text-dark-400 mt-1">
            {formData.bio.length}/500 characters
          </p>
        </div>

        {/* Avatar Upload */}
        <div className="mt-6">
          <label className="block text-sm font-medium text-dark-200 mb-2 flex items-center">
            <SafeIcon icon={FiImage} className="mr-2" />
            Profile Avatar
          </label>
          <AvatarUpload 
            onAvatarChange={(avatarUrl) => {
              setFormData(prev => ({ ...prev, avatar: avatarUrl }));
            }}
          />
        </div>
      </div>

      {/* Theme Selection */}
      <div className="glass-dark p-6 rounded-xl border border-dark-700">
        <h3 className="text-lg font-semibold text-lime-bright mb-6 flex items-center">
          <SafeIcon icon={FiStar} className="mr-2" />
          Theme
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {themes.map((theme) => (
            <label
              key={theme.id}
              className={`glass p-4 rounded-lg border-2 cursor-pointer transition-all ${
                formData.theme === theme.id
                  ? 'border-lime-500 bg-lime-500/10 shadow-glow-lime-sm'
                  : 'border-dark-700 hover:border-dark-600'
              }`}
            >
              <input
                type="radio"
                name="theme"
                value={theme.id}
                checked={formData.theme === theme.id}
                onChange={handleChange}
                className="sr-only"
              />
              <div className="font-medium text-white mb-1">{theme.name}</div>
              <div className="text-sm text-dark-300">{theme.description}</div>
            </label>
          ))}
        </div>
      </div>

      {/* Featured Section */}
      <div className="glass-dark p-6 rounded-xl border border-dark-700">
        <h3 className="text-lg font-semibold text-lime-bright mb-6 flex items-center">
          <SafeIcon icon={FiStar} className="mr-2" />
          Featured Section
        </h3>
        <div className="space-y-4">
          <div>
            <label htmlFor="featured_title" className="block text-sm font-medium text-dark-200 mb-2">
              Featured Title
            </label>
            <input
              type="text"
              id="featured_title"
              name="featured_title"
              value={formData.featured_title}
              onChange={handleChange}
              className="w-full px-3 py-2 glass rounded-lg text-white placeholder-dark-500 focus:ring-2 focus:ring-lime-500 focus:border-lime-500 border border-dark-700"
              placeholder="Featured Content"
            />
          </div>
          <div>
            <label htmlFor="featured_type" className="block text-sm font-medium text-dark-200 mb-2">
              Featured Type
            </label>
            <select
              id="featured_type"
              name="featured_type"
              value={formData.featured_type}
              onChange={handleChange}
              className="w-full px-3 py-2 glass rounded-lg text-white focus:ring-2 focus:ring-lime-500 focus:border-lime-500 border border-dark-700"
            >
              <option value="media">Media</option>
              <option value="products">Products</option>
            </select>
          </div>
        </div>
      </div>

      {/* SEO Settings */}
      <div className="glass-dark p-6 rounded-xl border border-dark-700">
        <h3 className="text-lg font-semibold text-lime-bright mb-6 flex items-center">
          <SafeIcon icon={FiSearch} className="mr-2" />
          SEO Settings
        </h3>
        <div className="space-y-4">
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="is_public"
              name="is_public"
              checked={formData.is_public}
              onChange={handleChange}
              className="w-4 h-4 text-lime-600 border-dark-600 rounded focus:ring-lime-500 bg-dark-800"
            />
            <label htmlFor="is_public" className="flex items-center text-sm font-medium text-dark-200">
              <SafeIcon icon={formData.is_public ? FiEye : FiEyeOff} className="mr-2 text-dark-400" />
              Make profile public
            </label>
          </div>
          <p className="text-xs text-dark-400">
            Public profiles can be discovered and indexed by search engines
          </p>

          <div>
            <label htmlFor="seo_title" className="block text-sm font-medium text-dark-200 mb-2">
              Custom Meta Title
            </label>
            <input
              type="text"
              id="seo_title"
              name="seo_title"
              value={formData.seo_title}
              onChange={handleChange}
              maxLength={60}
              className="w-full px-3 py-2 glass rounded-lg text-white placeholder-dark-500 focus:ring-2 focus:ring-lime-500 focus:border-lime-500 border border-dark-700"
              placeholder="Custom page title for SEO"
            />
            <p className="text-xs text-dark-400 mt-1">
              {formData.seo_title.length}/60 characters (optional)
            </p>
          </div>

          <div>
            <label htmlFor="seo_description" className="block text-sm font-medium text-dark-200 mb-2">
              Custom Meta Description
            </label>
            <textarea
              id="seo_description"
              name="seo_description"
              value={formData.seo_description}
              onChange={handleChange}
              rows={3}
              maxLength={160}
              className="w-full px-3 py-2 glass rounded-lg text-white placeholder-dark-500 focus:ring-2 focus:ring-lime-500 focus:border-lime-500 border border-dark-700 resize-none"
              placeholder="Custom description for search engines"
            />
            <p className="text-xs text-dark-400 mt-1">
              {formData.seo_description.length}/160 characters (optional)
            </p>
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={saving || loading || usernameChecking || usernameAvailable === false}
          className="glass-lime text-lime-400 px-6 py-3 rounded-lg font-medium hover:text-lime-300 transition-colors border border-lime-500/50 hover:border-lime-400/70 shadow-glow-lime-sm hover:shadow-glow-lime disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
        >
          <SafeIcon icon={FiSave} className="mr-2" />
          {saving ? 'Saving...' : 'Save Profile'}
        </button>
      </div>
    </form>
  );
};

export default ProfileEditor;
